/**
 * Repository Layer - React Query hooks for authentication operations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type {
  SyncGooglePicturePayload,
  UpdatePasswordPayload,
} from '@/api/adapters/auth.adapter'
import { changePassword, syncGooglePicture } from '@/api/services/auth.service'
import { queryKeys } from '@/lib/query-client'

/**
 * Hook to change a user's password
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string
      payload: UpdatePasswordPayload
    }) => changePassword(userId, payload),
    onSuccess: () => {
      toast.success('Password updated successfully')
    },
    onError: (error: unknown) => {
      console.error('Failed to change password:', error)
      toast.error(
        'Failed to update password. Check your current password and try again.',
      )
    },
  })
}

/**
 * Hook to push the signed-in user's Google profile picture to the API, which
 * stores a copy in the public bucket (when the user has Google sync enabled).
 * Silent by design: it runs in the background right after login.
 */
export function useSyncGooglePicture() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SyncGooglePicturePayload) =>
      syncGooglePicture(payload),
    onSuccess: () => {
      // Profile and backoffice user lists read `picture` from this cache
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    },
    onError: (error: unknown) => {
      console.error('Failed to sync Google profile picture:', error)
    },
  })
}
